(function (root, factory) {
  const api = factory();
  if (typeof module === 'object' && module.exports) module.exports = api;
  else root.EdiBotProductLogic = api;
})(typeof self !== 'undefined' ? self : this, function () {
  'use strict';

  const clone = (value) => JSON.parse(JSON.stringify(value));

  function createState(config) {
    const source = clone(config);
    const setIds = Object.keys(source.sets || {});
    const colors = Array.isArray(source.colors) ? source.colors : [];
    const selectedSet = source.sets[source.defaultSet] ? source.defaultSet : setIds[0];
    const selectedColor = colors.includes(source.defaultColor) ? source.defaultColor : colors[0];

    if (!selectedSet) throw new Error('EdiBot configuration must define at least one set.');
    if (!selectedColor) throw new Error('EdiBot configuration must define at least one color.');

    source.variants = Array.isArray(source.variants) ? source.variants : [];
    return {
      config: source,
      selectedSet,
      selectedColor,
      inkCareSelected: false,
      creatorRewardsSelected: false,
    };
  }

  function selectSet(state, setId) {
    if (!state.config.sets[setId]) return state;
    return { ...state, selectedSet: setId, inkCareSelected: false };
  }

  function selectColor(state, color) {
    if (!state.config.colors.includes(color)) return state;
    return { ...state, selectedColor: color };
  }

  function setInkCare(state, selected) {
    const set = state.config.sets[state.selectedSet];
    return { ...state, inkCareSelected: set.inkCareIncluded ? false : Boolean(selected) };
  }

  function setCreatorRewards(state, selected) {
    return { ...state, creatorRewardsSelected: Boolean(selected) };
  }

  function getSetPrice(state, setId) {
    const set = state.config.sets[setId];
    if (!set) return null;
    const variant = state.config.variants.find(
      (item) => item.set === setId && item.color === state.selectedColor
    ) || null;
    return {
      price: variant && Number.isFinite(variant.price) ? variant.price : set.price,
      compareAt: variant && Object.prototype.hasOwnProperty.call(variant, 'compareAt')
        ? variant.compareAt
        : set.compareAt,
      available: Boolean(variant && variant.available),
    };
  }

  function getDisplayPrice(state) {
    const set = state.config.sets[state.selectedSet];
    const livePrice = getSetPrice(state, state.selectedSet);
    const base = livePrice ? livePrice.price : set.price;
    const addOn = state.inkCareSelected ? state.config.inkCare.price : 0;
    return {
      total: base + addOn,
      compareAt: livePrice ? livePrice.compareAt : set.compareAt,
      inkCareIncluded: Boolean(set.inkCareIncluded),
    };
  }

  function getBoxContents(state) {
    return state.config.sets[state.selectedSet].contents.slice();
  }

  function resolveVariant(state) {
    return state.config.variants.find(
      (variant) => variant.set === state.selectedSet && variant.color === state.selectedColor
    ) || null;
  }

  function buildCartItems(state) {
    const variant = resolveVariant(state);
    if (!variant || !variant.available) throw new Error('Selected EdiBot variant is unavailable.');

    const productItem = { id: variant.id, quantity: 1 };
    if (state.creatorRewardsSelected) {
      productItem.properties = { 'Creator Rewards': 'Enrolled with purchase' };
    }

    const items = [productItem];
    if (state.inkCareSelected) {
      const inkId = state.config.inkCare.variantId;
      if (!inkId) throw new Error('Ink Care is not connected.');
      items.push({ id: inkId, quantity: 1 });
    }
    return items;
  }

  function getMediaNavigationIndex(currentIndex, count, key) {
    if (count <= 0) return -1;
    if (key === 'Home') return 0;
    if (key === 'End') return count - 1;
    if (key === 'ArrowRight' || key === 'ArrowDown') return (currentIndex + 1 + count) % count;
    if (key === 'ArrowLeft' || key === 'ArrowUp') return (currentIndex - 1 + count) % count;
    return currentIndex;
  }

  function getMediaPreloadOrder(activeIndex, count) {
    if (!Number.isInteger(count) || count <= 0) return [];
    if (!Number.isInteger(activeIndex) || activeIndex < 0 || activeIndex >= count) {
      return Array.from({ length: count }, (_, index) => index);
    }
    return Array.from({ length: Math.max(0, count - 1) }, (_, offset) =>
      (activeIndex + offset + 1) % count
    );
  }

  function formatMoney(cents, money) {
    const settings = money || {};
    const locale = settings.locale || undefined;
    const currencyCode = settings.currencyCode;
    const options = currencyCode
      ? { style: 'currency', currency: currencyCode }
      : { minimumFractionDigits: 2, maximumFractionDigits: 2 };
    return new Intl.NumberFormat(locale, options).format(cents / 100);
  }

  return {
    createState,
    selectSet,
    selectColor,
    setInkCare,
    setCreatorRewards,
    getSetPrice,
    getDisplayPrice,
    getBoxContents,
    resolveVariant,
    buildCartItems,
    getMediaNavigationIndex,
    getMediaPreloadOrder,
    formatMoney,
  };
});
